import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';

const inp = 'w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm';
export const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const split = (gross, pct) => {
  const g = Math.round(gross * 100);
  const fee = Math.round((g * pct) / 100 + 1e-9);
  return [fee / 100, (g - fee) / 100];
};

const NetworkSelect = ({ value, onChange, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className={inp} data-testid={testid}>
    <option value="base">Base (real money)</option>
    <option value="sepolia">Sepolia (test network)</option>
  </select>
);

const Breakdown = ({ gross, pct, outLabel, testid }) => {
  const [fee, net] = split(gross || 0, pct);
  return (
    <div className="text-xs text-slate-400 bg-slate-800/60 rounded-lg p-3 space-y-1" data-testid={testid}>
      <p className="flex justify-between"><span>Desk fee ({pct}%)</span><span>{money(fee)}</span></p>
      <p className="flex justify-between text-white font-semibold"><span>{outLabel}</span><span>{money(net)}</span></p>
    </div>
  );
};

const Submit = ({ busy, disabled, children, testid, onClick }) => (
  <button type="button" disabled={busy || disabled} onClick={onClick} data-testid={testid}
    className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-colors">
    {busy && <Loader2 className="w-4 h-4 animate-spin" />} {children}
  </button>
);

export const DeskMintForm = ({ info, busy, onSubmit }) => {
  const [f, setF] = useState({ network: 'base', amount_usd: '', payment_method: 'usdc', payment_reference: '' });
  const set = (k, v) => setF({ ...f, [k]: v });
  const amount = parseFloat(f.amount_usd);
  return (
    <div className="space-y-3" data-testid="desk-mint-form">
      <NetworkSelect value={f.network} onChange={(v) => set('network', v)} testid="desk-mint-network" />
      <input type="number" min="1" step="0.01" placeholder="Amount you pay (USD)" value={f.amount_usd} onChange={(e) => set('amount_usd', e.target.value)} className={inp} data-testid="desk-mint-amount" />
      <select value={f.payment_method} onChange={(e) => set('payment_method', e.target.value)} className={inp} data-testid="desk-mint-method">
        <option value="usdc">USDC sent to the treasury wallet</option>
        <option value="bank">Bank transfer / wire</option>
      </select>
      {f.payment_method === 'usdc' && info?.treasury_wallet && <p className="text-xs text-slate-400 break-all" data-testid="desk-treasury-wallet">Send USDC on {f.network} to <span className="text-emerald-300 font-mono">{info.treasury_wallet}</span></p>}
      <input placeholder={f.payment_method === 'usdc' ? 'USDC transaction hash' : 'Bank transfer reference'} value={f.payment_reference} onChange={(e) => set('payment_reference', e.target.value)} className={inp} data-testid="desk-mint-reference" />
      <Breakdown gross={amount} pct={info?.mint_fee_pct ?? 0.5} outLabel="You receive (BETH USD)" testid="desk-mint-breakdown" />
      <Submit busy={busy} disabled={!(amount >= 1) || f.payment_reference.trim().length < 3} testid="desk-mint-submit"
        onClick={() => onSubmit('mint', { ...f, amount_usd: amount })}>Request BETH USD</Submit>
    </div>
  );
};

export const DeskRedeemForm = ({ info, busy, onSubmit }) => {
  const [f, setF] = useState({ network: 'base', amount_beth: '', payout_details: '' });
  const set = (k, v) => setF({ ...f, [k]: v });
  const amount = parseFloat(f.amount_beth);
  return (
    <div className="space-y-3" data-testid="desk-redeem-form">
      <NetworkSelect value={f.network} onChange={(v) => set('network', v)} testid="desk-redeem-network" />
      <input type="number" min="1" step="0.01" placeholder="BETH USD to cash out" value={f.amount_beth} onChange={(e) => set('amount_beth', e.target.value)} className={inp} data-testid="desk-redeem-amount" />
      <textarea rows={2} placeholder="Where to pay you (bank details or a USDC wallet address)" value={f.payout_details} onChange={(e) => set('payout_details', e.target.value)} className={inp} data-testid="desk-redeem-payout" />
      <Breakdown gross={amount} pct={info?.redeem_fee_pct ?? 0.5} outLabel="You are paid (USD)" testid="desk-redeem-breakdown" />
      <Submit busy={busy} disabled={!(amount >= 1) || f.payout_details.trim().length < 3} testid="desk-redeem-submit"
        onClick={() => onSubmit('redeem', { ...f, amount_beth: amount })}>Request cash-out</Submit>
    </div>
  );
};
